import { useState } from 'react';
import { Card } from '../types';
import HomeCard from './cards/HomeCard';
import CommunityCard from './cards/CommunityCard';
import FloorPlanCard from './cards/FloorPlanCard';

interface CardCarouselProps {
  cards: Card[];
}

export default function CardCarousel({ cards }: CardCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);

  if (!cards || cards.length === 0) return null;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : cards.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < cards.length - 1 ? prev + 1 : 0));
  };

  const renderCard = (card: Card) => {
    switch (card.type) {
      case 'home':
        return <HomeCard key={`home-${card.home_id}`} card={card} onFormToggle={setIsFormOpen} />;
      case 'community':
        return <CommunityCard key={`community-${card.community_id}`} card={card} />;
      case 'floor_plan':
        return <FloorPlanCard key={`plan-${card.plan_id}`} card={card} onFormToggle={setIsFormOpen} />;
      default:
        return null;
    }
  };

  return (
    <div className="relative w-full mt-2">
      {/* Carousel Container */}
      <div className="relative overflow-hidden rounded-lg">
        {/* Current Card */}
        <div className="transition-all duration-300 ease-in-out">
          {renderCard(cards[currentIndex])}
        </div>

        {/* Navigation Arrows (only if more than 1 card and form is not open) */}
        {cards.length > 1 && !isFormOpen && (
          <>
            {/* Previous Button */}
            <button
              onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Previous"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Next"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Dots Indicator (hide when form is open) */}
      {cards.length > 1 && !isFormOpen && (
        <div className="flex justify-center gap-2 mt-3">
          {cards.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-[#a4ac3a] w-6'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Counter (hide when form is open) */}
      {cards.length > 1 && !isFormOpen && (
        <div className="text-center mt-2 text-sm text-gray-500">
          {currentIndex + 1} / {cards.length}
        </div>
      )}
    </div>
  );
}
